import React, { useState, useEffect } from 'react';
import { X, DollarSign, Check, AlertCircle, Plus, Trash2, Edit2, Save } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';
import { PaymentMethod, Order, Payment } from '../types';
import { addPayments, getOrderByInvoiceId, updatePayment, deletePayment, invoiceAPI } from '../services/api';

interface AddPaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    invoiceId: string;
    onSuccess: () => void;
}

const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
    isOpen,
    onClose,
    invoiceId,
    onSuccess
}) => {
    const { t, isRTL } = useI18n();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [order, setOrder] = useState<Order | null>(null);
    const [fetchingOrder, setFetchingOrder] = useState(false);

    // New payments state
    const [newPayments, setNewPayments] = useState<Partial<Payment>[]>([
        { amount: 0, method: PaymentMethod.CASH, description: '' }
    ]);

    // Editing state
    const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<Payment>>({});

    useEffect(() => {
        if (isOpen && invoiceId) {
            fetchOrderDetails();
        } else {
            // Reset state when closed
            setOrder(null);
            setNewPayments([{ amount: 0, method: PaymentMethod.CASH, description: '' }]);
            setError(null);
            setSuccess(false);
            setEditingPaymentId(null);
        }
    }, [isOpen, invoiceId]);

    const fetchOrderDetails = async () => {
        setFetchingOrder(true);
        setError(null);
        try {
            const orderData = await getOrderByInvoiceId(invoiceId);
            setOrder(orderData);

            // Calculate remaining balance
            const totalPaid = orderData.paymentHistory.reduce((sum, p) => sum + p.amount, 0);
            const remaining = Math.max(0, orderData.totalAmount - totalPaid);

            // Pre-fill first payment with remaining balance
            setNewPayments([{ amount: parseFloat(remaining.toFixed(2)), method: PaymentMethod.CASH, description: '' }]);
        } catch (err) {
            console.error('Failed to fetch order details:', err);
            setError(t('payment.errorFetchingOrder'));
        } finally {
            setFetchingOrder(false);
        }
    };

    if (!isOpen) return null;

    // Calculate totals
    const totalPaid = order ? order.paymentHistory.reduce((sum, p) => sum + p.amount, 0) : 0;
    const totalWithVat = order ? order.totalAmount : 0;
    const remaining = Math.max(0, totalWithVat - totalPaid);
    const isFullyPaid = remaining <= 0.01;

    // Calculate total of new payments
    const totalNewPayments = newPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

    const handleAddPaymentRow = () => {
        setNewPayments([...newPayments, { amount: 0, method: PaymentMethod.CASH, description: '' }]);
    };

    const handleRemovePaymentRow = (index: number) => {
        const updated = [...newPayments];
        updated.splice(index, 1);
        setNewPayments(updated);
    };

    const handlePaymentChange = (index: number, field: keyof Payment, value: any) => {
        const updated = [...newPayments];
        updated[index] = { ...updated[index], [field]: value };
        setNewPayments(updated);
    };

    const handleEditClick = (payment: Payment) => {
        setEditingPaymentId(payment.id);
        setEditForm({ ...payment });
    };

    const handleCancelEdit = () => {
        setEditingPaymentId(null);
        setEditForm({});
    };

    const handleSaveEdit = async () => {
        if (!order || !editingPaymentId) return;

        try {
            setLoading(true);
            const updatedOrder = await updatePayment(order.id, editingPaymentId, editForm as Payment);
            setOrder(updatedOrder);
            setEditingPaymentId(null);
        } catch (err) {
            console.error('Failed to update payment:', err);
            setError(t('payment.errorUpdating'));
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePayment = async (paymentId: string) => {
        if (!order || !window.confirm(t('common.confirmDelete'))) return;

        try {
            setLoading(true);
            const updatedOrder = await deletePayment(order.id, paymentId);
            setOrder(updatedOrder);

            // Recalculate remaining for pre-fill
            const newTotalPaid = updatedOrder.paymentHistory.reduce((sum, p) => sum + p.amount, 0);
            const newRemaining = Math.max(0, totalWithVat - newTotalPaid);
            setNewPayments([{ amount: parseFloat(newRemaining.toFixed(2)), method: PaymentMethod.CASH, description: '' }]);

        } catch (err) {
            console.error('Failed to delete payment:', err);
            setError(t('payment.errorDeleting'));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (totalNewPayments <= 0) {
            setError(t('payment.invalidAmount'));
            return;
        }

        if (totalNewPayments > remaining + 0.01) {
            setError(t('payment.amountExceedsRemaining'));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            if (order) {
                // Filter out invalid payments and cast to Payment type
                const validPayments = newPayments.filter(p => p.amount && p.amount > 0) as Payment[];
                await addPayments(order.id, validPayments);

                setSuccess(true);
                setTimeout(() => {
                    onSuccess();
                    onClose();
                }, 1500);
            }
        } catch (err) {
            console.error('Failed to add payments:', err);
            setError(t('payment.errorAdding'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
                </div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div
                    className={`inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full ${isRTL ? 'rtl' : 'ltr'}`}
                    style={{ width: '43rem', maxWidth: '95vw' }}
                    dir={isRTL ? 'rtl' : 'ltr'}
                >
                    {fetchingOrder ? (
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-2 text-gray-500">{t('common.loading')}</p>
                        </div>
                    ) : success ? (
                        <div className="p-6 text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                                <Check className="h-6 w-6 text-green-600" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('payment.successTitle')}</h3>
                            <p className="text-sm text-gray-500">{t('payment.successMessage')}</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10 ${isRTL ? 'sm:ml-4' : 'sm:mr-4'}`}>
                                        <DollarSign className="h-6 w-6 text-blue-600" />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                            {t('payment.addPayment')}
                                        </h3>

                                        {/* Remaining Balance Info */}
                                        {order && (
                                            <div className={`mt-4 p-3 rounded-md ${isFullyPaid ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
                                                <div className="flex justify-between items-center">
                                                    <span className={`text-sm font-medium ${isFullyPaid ? 'text-green-800' : 'text-blue-800'}`}>
                                                        {isFullyPaid ? t('payment.fullyPaid') : t('stock.orders.remainingBalance')}
                                                    </span>
                                                    <span className={`text-lg font-bold ${isFullyPaid ? 'text-green-800' : 'text-blue-800'}`}>
                                                        {remaining.toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {error && (
                                            <div className="mt-2 p-2 bg-red-50 rounded-md flex items-center gap-2 text-red-600 text-sm">
                                                <AlertCircle className="w-4 h-4" />
                                                <span>{error}</span>
                                            </div>
                                        )}

                                        {/* Payment History Table */}
                                        {order && order.paymentHistory.length > 0 && (
                                            <div className="mt-6">
                                                <h4 className="text-sm font-medium text-gray-900 mb-2">{t('payment.history')}</h4>
                                                <div className="bg-gray-50 rounded-md border border-gray-200 max-h-60 overflow-y-auto">
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-100">
                                                            <tr>
                                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.date')}</th>
                                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('payment.amount')}</th>
                                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('payment.method')}</th>
                                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('payment.description')}</th>
                                                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                            {order.paymentHistory.map((payment) => (
                                                                <tr key={payment.id}>
                                                                    {editingPaymentId === payment.id ? (
                                                                        <>
                                                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                                                                {new Date(payment.date).toLocaleDateString()}
                                                                            </td>
                                                                            <td className="px-3 py-2 whitespace-nowrap text-sm">
                                                                                <input
                                                                                    type="number"
                                                                                    value={editForm.amount}
                                                                                    onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) })}
                                                                                    className="w-24 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-xs"
                                                                                />
                                                                            </td>
                                                                            <td className="px-3 py-2 whitespace-nowrap text-sm">
                                                                                <select
                                                                                    value={editForm.method}
                                                                                    onChange={(e) => setEditForm({ ...editForm, method: e.target.value as PaymentMethod })}
                                                                                    className="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-xs"
                                                                                >
                                                                                    {Object.values(PaymentMethod).map((m) => (
                                                                                        <option key={m} value={m}>{t(`common.payments.${m}`)}</option>
                                                                                    ))}
                                                                                </select>
                                                                            </td>
                                                                            <td className="px-3 py-2 whitespace-nowrap text-sm">
                                                                                <input
                                                                                    type="text"
                                                                                    value={editForm.description || ''}
                                                                                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-xs"
                                                                                />
                                                                            </td>
                                                                            <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                                                                                <button type="button" onClick={handleSaveEdit} className="text-green-600 hover:text-green-900 mr-2">
                                                                                    <Save className="h-4 w-4" />
                                                                                </button>
                                                                                <button type="button" onClick={handleCancelEdit} className="text-red-600 hover:text-red-900">
                                                                                    <X className="h-4 w-4" />
                                                                                </button>
                                                                            </td>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                                                                                {new Date(payment.date).toLocaleDateString()}
                                                                            </td>
                                                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                                                {payment.amount.toFixed(2)}
                                                                            </td>
                                                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                                                                {t(`common.payments.${payment.method}`)}
                                                                            </td>
                                                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 truncate max-w-[150px]">
                                                                                {payment.description}
                                                                            </td>
                                                                            <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                                                                                <button type="button" onClick={() => handleEditClick(payment)} className="text-blue-600 hover:text-blue-900 mr-2">
                                                                                    <Edit2 className="h-4 w-4" />
                                                                                </button>
                                                                                <button type="button" onClick={() => handleDeletePayment(payment.id)} className="text-red-600 hover:text-red-900">
                                                                                    <Trash2 className="h-4 w-4" />
                                                                                </button>
                                                                            </td>
                                                                        </>
                                                                    )}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {/* New Payments Section */}
                                        <div className="mt-6">
                                            <div className="flex justify-between items-center mb-2">
                                                <h4 className="text-sm font-medium text-gray-900">{t('payment.newPayments')}</h4>
                                                <button
                                                    type="button"
                                                    onClick={handleAddPaymentRow}
                                                    disabled={isFullyPaid}
                                                    className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                                >
                                                    <Plus className="h-3 w-3 mr-1" />
                                                    {t('common.add')}
                                                </button>
                                            </div>

                                            <div className="space-y-2">
                                                {newPayments.map((payment, index) => (
                                                    <div key={index} className="flex gap-2 items-start">
                                                        <div className="w-1/4">
                                                            <input
                                                                type="number"
                                                                placeholder={t('payment.amount')}
                                                                min="0.01"
                                                                step="0.01"
                                                                disabled={isFullyPaid}
                                                                value={payment.amount || ''}
                                                                onChange={(e) => handlePaymentChange(index, 'amount', parseFloat(e.target.value))}
                                                                className="block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                            />
                                                        </div>
                                                        <div className="w-1/4">
                                                            <select
                                                                value={payment.method}
                                                                disabled={isFullyPaid}
                                                                onChange={(e) => handlePaymentChange(index, 'method', e.target.value as PaymentMethod)}
                                                                className="block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                            >
                                                                {Object.values(PaymentMethod).map((m) => (
                                                                    <option key={m} value={m}>{t(`common.payments.${m}`)}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="flex-1">
                                                            <input
                                                                type="text"
                                                                placeholder={t('payment.description')}
                                                                disabled={isFullyPaid}
                                                                value={payment.description || ''}
                                                                onChange={(e) => handlePaymentChange(index, 'description', e.target.value)}
                                                                className="block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                            />
                                                        </div>
                                                        {newPayments.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemovePaymentRow(index)}
                                                                className="mt-1.5 text-red-500 hover:text-red-700"
                                                            >
                                                                <X className="h-5 w-5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Total of new payments */}
                                            <div className="mt-2 text-right text-sm text-gray-500">
                                                Total: <span className="font-bold text-gray-900">{totalNewPayments.toFixed(2)}</span>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="submit"
                                    disabled={loading || isFullyPaid || totalNewPayments <= 0}
                                    className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm ${loading || isFullyPaid || totalNewPayments <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {loading ? t('common.processing') : t('common.submit')}
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    disabled={loading}
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                >
                                    {t('common.cancel')}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddPaymentModal;
